import { type ExplorePlace, exploreView } from '@mollusk/shared';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { BarChart } from '../../components/charts/BarChart';
import { MONO } from '../../theme';

const pct = (s: number) => `${Math.round(s * 100)}%`;

/**
 * Explore (#172): permits by year for the last 10 years beside the place's
 * top tags. Clicking a tag switches the chart to that tag (last 5 years).
 * Render with `key={place.id}` so a new place starts on all permits.
 */
export function ExploreView({ place, thisYear }: { place: ExplorePlace; thisYear: number }) {
  const [tag, setTag] = useState<string | null>(null);
  const view = exploreView(place, tag, thisYear);
  const title = tag
    ? `Permits tagged ${tag} in ${place.name}, last 5 years`
    : `Permits in ${place.name}, last 10 years`;
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
        alignItems: 'start',
      }}
    >
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{title}</Typography>
          {tag ? (
            <Button size="small" variant="outlined" onClick={() => setTag(null)}>
              Show all permits
            </Button>
          ) : null}
        </Box>
        <BarChart
          data={view.series.map((s) => ({ label: String(s.year), n: s.n }))}
          title={title}
        />
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 600 }}>Top tags</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Share of permits (last 10 years). Click a tag to chart it.
        </Typography>
        {view.topTags.map((t) => (
          <ButtonBase
            key={t.tag}
            onClick={() => setTag(t.tag)}
            aria-pressed={tag === t.tag}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 48px',
              gap: 0.5,
              width: '100%',
              textAlign: 'left',
              py: 0.75,
              px: 1,
              borderRadius: 1,
              bgcolor: tag === t.tag ? 'action.selected' : undefined,
            }}
          >
            <Box>
              <Typography sx={{ fontFamily: MONO, fontSize: 13 }}>{t.tag}</Typography>
              <LinearProgress
                variant="determinate"
                value={t.share * 100}
                sx={{ height: 5, borderRadius: 3, mt: 0.5 }}
              />
            </Box>
            <Typography variant="body2" sx={{ textAlign: 'right', alignSelf: 'center' }}>
              {pct(t.share)}
            </Typography>
          </ButtonBase>
        ))}
        {view.topTags.length === 0 ? (
          <Typography color="text.secondary">No tagged permits.</Typography>
        ) : null}
      </Paper>
    </Box>
  );
}
